import { useState, useEffect } from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { toast } from 'sonner';
import api from '../lib/api';

const formatPrice = (value) => {
    if (value === undefined || value === null) return '0,00';
    return value.toFixed(2).replace('.', ',');
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const ScrapV2 = () => {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({ total_items: 0, total_value: 0, unique_products: 0 });
    
    // Scan scrap modal
    const [showScrapModal, setShowScrapModal] = useState(false);
    const [scannedProduct, setScannedProduct] = useState(null);
    const [scrapQty, setScrapQty] = useState(1);
    const [scrapReason, setScrapReason] = useState('Deteriorat');
    const [showScanner, setShowScanner] = useState(false);

    useEffect(() => {
        // Ensure V2 version is set
        loadScrap();
        
        // Listen for scan event
        const handleScan = () => {
            setShowScanner(true);
        };
        window.addEventListener('v2-scan-scrap', handleScan);
        
        return () => {
            window.removeEventListener('v2-scan-scrap', handleScan);
        };
    }, []);
    
    const handleScannedBarcode = async (barcode) => {
        setShowScanner(false);
        // Find product in store
        try {
            const res = await api.get('/store/v2');
            const storeItems = res.data || [];
            const found = storeItems.find(item => item.barcode === barcode);
            
            if (found) {
                setScannedProduct(found);
                setScrapQty(1);
                setScrapReason('Deteriorat');
                setShowScrapModal(true);
            } else {
                toast.error(`Produsul cu codul ${barcode} nu există în magazin`);
            }
        } catch (err) {
            toast.error('Eroare la căutare produs');
        }
    };
    
    const handleMoveToScrap = async () => {
        if (scrapQty < 1 || scrapQty > scannedProduct.quantity) {
            toast.error('Cantitate invalidă');
            return;
        }
        try {
            await api.post('/scrap/v2/move', {
                store_item_barcode: scannedProduct.barcode,
                quantity: scrapQty,
                reason: scrapReason
            });
            toast.success(`${scrapQty} buc mutate în SCRAP!`);
            setShowScrapModal(false);
            loadScrap();
            
            // Reopen scanner for continuous scanning
            setShowScanner(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
    };

    const loadScrap = async () => {
        setLoading(true);
        try {
            const [itemsRes, summaryRes] = await Promise.all([
                api.get('/scrap/v2'),
                api.get('/scrap/v2/summary')
            ]);
            setItems(itemsRes.data || []);
            setSummary(summaryRes.data || { total_items: 0, total_value: 0, unique_products: 0 });
        } catch (err) {
            console.error('Error loading scrap:', err);
        }
        setLoading(false);
    };

    if (loading) {
        return (
            <div className="pb-24">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        SCRAP
                    </h1>
                </div>
                <div className="space-y-3">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="pb-24">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    SCRAP
                </h1>
                <p className="text-zinc-500 mt-1">Produse deteriorate / retrase</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-white p-4 rounded-xl border border-zinc-200 text-center">
                    <p className="text-xs text-zinc-500 mb-1">Produse Unice</p>
                    <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {summary.unique_products}
                    </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-zinc-200 text-center">
                    <p className="text-xs text-zinc-500 mb-1">Total Bucăți</p>
                    <p className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {summary.total_items}
                    </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-zinc-200 text-center">
                    <p className="text-xs text-zinc-500 mb-1">Valoare Pierdută</p>
                    <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatPrice(summary.total_value)}
                    </p>
                </div>
            </div>

            {/* Items List */}
            {items.length === 0 ? (
                <div className="bg-zinc-100 border border-zinc-200 rounded-xl p-8 text-center">
                    <Trash2 size={48} className="mx-auto text-zinc-400 mb-3" />
                    <h2 className="font-bold text-zinc-700 mb-2">Nicio pierdere</h2>
                    <p className="text-zinc-500">Produsele deteriorate vor apărea aici</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((item) => (
                        <div
                            key={item.id}
                            className="bg-white border border-zinc-200 rounded-lg p-3 flex items-center justify-between"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                    <AlertTriangle size={18} className="text-red-500" />
                                </div>
                                <div>
                                    <p className="font-medium text-zinc-900">{item.name}</p>
                                    <p className="text-xs text-zinc-400">
                                        {item.reason} • {formatDate(item.scrapped_at)}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-lg font-bold text-zinc-900">{item.quantity} buc</p>
                                <p className="text-xs text-red-500">
                                    -{formatPrice(item.quantity * (item.purchase_price || 0))} RON
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Scrap Modal from Scan */}
            <Dialog open={showScrapModal} onOpenChange={setShowScrapModal}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 size={20} />
                            SCRAP Produs
                        </DialogTitle>
                    </DialogHeader>
                    
                    {scannedProduct && (
                        <div className="space-y-4 mt-4">
                            {/* Product info */}
                            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                                <p className="font-medium text-zinc-900">{scannedProduct.name}</p>
                                <p className="text-sm text-zinc-500">Disponibil: {scannedProduct.quantity} buc</p>
                                <p className="text-xs text-zinc-400 font-mono">{scannedProduct.barcode}</p>
                            </div>
                            
                            {/* Quantity */}
                            <div>
                                <Label>Cantitate</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={scannedProduct.quantity}
                                    value={scrapQty}
                                    onChange={(e) => setScrapQty(parseInt(e.target.value) || 1)}
                                    className="mt-1"
                                    autoFocus
                                />
                            </div>
                            
                            {/* Reason */}
                            <div>
                                <Label>Motiv</Label>
                                <Select value={scrapReason} onValueChange={setScrapReason}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Deteriorat">Deteriorat</SelectItem>
                                        <SelectItem value="Spart">Spart</SelectItem>
                                        <SelectItem value="Expirat">Expirat</SelectItem>
                                        <SelectItem value="Defect">Defect</SelectItem>
                                        <SelectItem value="Altul">Altul</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <Button variant="outline" onClick={() => setShowScrapModal(false)} className="flex-1">
                                    Renunță
                                </Button>
                                <Button 
                                    onClick={handleMoveToScrap} 
                                    className="flex-1 bg-red-500 hover:bg-red-600"
                                >
                                    <Trash2 size={16} className="mr-2" />
                                    SCRAP
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Barcode Scanner */}
            <BarcodeScanner
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleScannedBarcode}
            />
        </div>
    );
};

export default ScrapV2;
